import React, { useEffect, useState } from 'react';
import { useLocation, useNavigate } from 'react-router-dom';
import { Button, Spinner, Toast } from '../../lib';
import apiServices from '../../services/apiServices';
import {
  GetStepUpStatus,
  IssueStepUpCode,
  StepUpStatusResponse,
  VerifyStepUpCode,
} from '../../services/dtos';

// D2 step-up verify page. Documents (D3) demand a recently-verified identity
// on top of the login session: a 6-digit code over the email or mobile number
// on file. Document endpoints answer 403 "StepUpRequired" and the SPA routes
// here with ?returnTo=<path>; after verifying, Continue goes back there.
// All limits (10-min codes, 5 attempts, 3 sends/hour, ~30-min elevation) are
// enforced server-side — this page just renders the state machine.

function minutes(seconds?: number): number {
  return Math.max(1, Math.round((seconds ?? 0) / 60));
}

const StepUpVerifyPage: React.FC = () => {
  const navigate = useNavigate();
  const location = useLocation();

  const [status, setStatus] = useState<StepUpStatusResponse | null>(null);
  const [loadFailed, setLoadFailed] = useState(false);
  const [error, setError] = useState<string | null>(null);
  const [busy, setBusy] = useState(false);
  const [code, setCode] = useState('');
  const [pickChannel, setPickChannel] = useState(false); // "send a new code"

  // only ever bounce back to an internal path — never an absolute URL
  const rawReturnTo = new URLSearchParams(location.search).get('returnTo');
  const returnTo = rawReturnTo && rawReturnTo.startsWith('/') ? rawReturnTo : '/';

  useEffect(() => {
    apiServices.client
      .get(new GetStepUpStatus())
      .then((s: StepUpStatusResponse) => setStatus(s))
      .catch(() => setLoadFailed(true));
  }, []);

  const issue = (channel: 'Email' | 'Text') => {
    if (busy) return;
    setBusy(true);
    setError(null);
    apiServices.client
      .post(new IssueStepUpCode({ channel }))
      .then((s: StepUpStatusResponse) => {
        setStatus(s);
        setPickChannel(false);
        setCode('');
      })
      .catch((e: any) =>
        setError(e?.responseStatus?.message || 'Sending the code failed — try again.')
      )
      .then(() => setBusy(false));
  };

  const verify = () => {
    if (busy || code.trim().length !== 6) return;
    setBusy(true);
    setError(null);
    apiServices.client
      .post(new VerifyStepUpCode({ code: code.trim() }))
      .then((s: StepUpStatusResponse) => {
        setStatus(s);
        setCode('');
      })
      .catch((e: any) =>
        setError(e?.responseStatus?.message || 'That code could not be verified — try again.')
      )
      .then(() => setBusy(false));
  };

  const channelButtons = status && (
    <div className="flex flex-col gap-3">
      {status.emailMasked && (
        <Button color="light" disabled={busy} onClick={() => issue('Email')}>
          Email me a code — {status.emailMasked}
        </Button>
      )}
      {status.phoneMasked && (
        <Button color="light" disabled={busy} onClick={() => issue('Text')}>
          Text me a code — {status.phoneMasked}
        </Button>
      )}
      {!status.emailMasked && !status.phoneMasked && (
        <p className="text-sm text-gray-600 dark:text-gray-300">
          We don&apos;t have an email address or mobile number on file for you —
          please contact the union office to update your contact information.
        </p>
      )}
    </div>
  );

  return (
    <div className="mx-auto w-full max-w-md flex flex-col gap-6 dark:text-white">
      <div>
        <h2 className="text-2xl font-bold">Verify it&apos;s you</h2>
        <p className="mt-1 text-sm text-gray-500 dark:text-gray-400">
          Union documents are protected — confirm your identity with a one-time
          code and you&apos;re set for about 30 minutes.
        </p>
      </div>

      {error && <Toast color="failure">{error}</Toast>}
      {loadFailed && (
        <Toast color="failure">Couldn&apos;t check your verification status — reload the page.</Toast>
      )}
      {!status && !loadFailed && <Spinner color="info" />}

      {status?.elevated && (
        <div className="rounded-lg border border-green-200 dark:border-green-800 bg-green-50 dark:bg-green-900 p-4 flex flex-col gap-3">
          <p className="text-sm font-medium text-green-800 dark:text-green-200">
            You&apos;re verified — your access lasts about{' '}
            {minutes(status.elevationSecondsLeft)} more minutes (it extends
            while you&apos;re reading).
          </p>
          <div>
            <Button size="sm" onClick={() => navigate(returnTo)}>
              Continue
            </Button>
          </div>
        </div>
      )}

      {status && !status.elevated && (!status.codePending || pickChannel) && (
        <div className="rounded-lg border border-gray-200 dark:border-gray-700 bg-white dark:bg-gray-900 p-4 flex flex-col gap-3">
          <h3 className="text-sm font-semibold">Where should we send your code?</h3>
          {channelButtons}
        </div>
      )}

      {status && !status.elevated && status.codePending && !pickChannel && (
        <div className="rounded-lg border border-gray-200 dark:border-gray-700 bg-white dark:bg-gray-900 p-4 flex flex-col gap-3">
          <p className="text-sm text-gray-700 dark:text-gray-300">
            Enter the 6-digit code we sent to{' '}
            <span className="font-medium">{status.codeDestinationMasked}</span>.
            It expires in about {minutes(status.codeSecondsLeft)} minutes.
          </p>
          <input
            autoFocus
            value={code}
            onChange={(e) => setCode(e.target.value.replace(/\D/g, '').slice(0, 6))}
            onKeyDown={(e) => e.key === 'Enter' && verify()}
            inputMode="numeric"
            autoComplete="one-time-code"
            placeholder="••••••"
            className="w-40 rounded-lg border border-gray-300 dark:border-gray-600 bg-white dark:bg-gray-800 px-3 py-2 text-center text-2xl tracking-[0.4em] font-mono dark:text-white"
          />
          <div className="flex items-center gap-4">
            <Button size="sm" disabled={busy || code.length !== 6} onClick={verify}>
              {busy ? 'Checking…' : 'Verify'}
            </Button>
            <button
              onClick={() => {
                setPickChannel(true);
                setError(null);
              }}
              className="text-xs font-medium text-indigo-600 hover:underline dark:text-indigo-400"
            >
              Didn&apos;t get it? Send a new code
            </button>
          </div>
        </div>
      )}
    </div>
  );
};

export default StepUpVerifyPage;
